import { useEffect, useMemo, useState } from "react";
import { Bot, Check, LoaderCircle, LockKeyhole, Sparkles, Volume2, VolumeX } from "lucide-react";
import { nanoid } from "nanoid";
import { Conversation, ConversationContent, ConversationEmptyState, ConversationScrollButton } from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { PromptInput, PromptInputBody, PromptInputFooter, PromptInputSubmit, PromptInputTextarea, PromptInputTools } from "@/components/ai-elements/prompt-input";
import { SpeechInput } from "@/components/ai-elements/speech-input";
import { Button } from "@/components/ui/button";
import { useProviderVault } from "@/context/ProviderContext";
import { useWallet } from "@/context/WalletContext";
import { forgePersonality, streamAvatarReply } from "@/lib/ai";
import { loadChat, saveChat } from "@/lib/browser-store";
import { personalityJson } from "@/lib/personality";
import { speakWithKokoro, transcribeWithWhisper } from "@/lib/voice";
import type { ChatMessage, PersonalityCard, TraitSelection } from "@/types";

export function ChatPanel({
  traits,
  personality,
  onPersonality,
  sessionId = "draft",
  onSpeaking,
}: {
  traits: TraitSelection;
  personality: PersonalityCard | null;
  onPersonality: (card: PersonalityCard) => void;
  sessionId?: string;
  onSpeaking?: (speaking: boolean) => void;
}) {
  const { address } = useWallet();
  const { secret, hasSavedSecret, unlock, busy: vaultBusy } = useProviderVault();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<"ready" | "submitted" | "streaming" | "error">("ready");
  const [error, setError] = useState<string | null>(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [forging, setForging] = useState(false);
  const [loadedChatKey, setLoadedChatKey] = useState<string | null>(null);
  const chatKey = useMemo(() => `chat:${address?.toLowerCase() ?? "guest"}:${sessionId}`, [address, sessionId]);

  useEffect(() => {
    let active = true;
    setMessages([]);
    setLoadedChatKey(null);
    loadChat(chatKey)
      .then((saved) => {
        if (active) {
          setMessages(saved);
          setLoadedChatKey(chatKey);
        }
      })
      .catch(() => {
        if (active) setLoadedChatKey(chatKey);
      });
    return () => { active = false; };
  }, [chatKey]);

  useEffect(() => {
    if (loadedChatKey === chatKey && messages.length) void saveChat(chatKey, messages);
  }, [messages, chatKey, loadedChatKey]);

  const userTurns = messages.filter((message) => message.role === "user").length;

  const handleSubmit = async ({ text }: { text: string }) => {
    const cleanText = text.trim();
    if (!cleanText || !secret || status === "streaming") return;
    setInput("");
    setError(null);
    setStatus("submitted");
    const userMessage: ChatMessage = { id: nanoid(), role: "user", content: cleanText.slice(0, 1_500), createdAt: Date.now() };
    const assistantId = nanoid();
    const assistantMessage: ChatMessage = { id: assistantId, role: "assistant", content: "", createdAt: Date.now() + 1 };
    const nextMessages = [...messages, userMessage];
    setMessages([...nextMessages, assistantMessage]);
    setStatus("streaming");
    try {
      const reply = await streamAvatarReply(secret, nextMessages, personality, traits, (content) => {
        setMessages([...nextMessages, { ...assistantMessage, content }]);
      });
      const complete = [...nextMessages, { ...assistantMessage, content: reply || "I’m still here. Tell me one more thing about you." }];
      setMessages(complete);
      setStatus("ready");
      if (voiceEnabled && reply) void speakWithKokoro(reply, onSpeaking);
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "The AI provider did not answer.");
      setMessages(nextMessages);
    }
  };

  const handleForge = async () => {
    if (!secret || userTurns < 5) return;
    setForging(true);
    setError(null);
    try {
      const card = await forgePersonality(secret, messages);
      personalityJson(card);
      onPersonality(card);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not forge the personality card.");
    } finally {
      setForging(false);
    }
  };

  return (
    <section className="flex min-h-[610px] flex-col overflow-hidden rounded-[24px] border border-white/10 bg-white/[.035]">
      <div className="flex items-center justify-between gap-3 border-b border-white/8 px-4 py-4 sm:px-5">
        <div><p className="text-[11px] font-bold tracking-[0.2em] text-pink-300/75">SOUL LINK</p><h2 className="mt-1 font-bold">Teach the personality</h2></div>
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold tracking-wider text-white/55">{Math.min(userTurns, 5)}/5 TURNS</span>
          <Button aria-label={voiceEnabled ? "Disable voice replies" : "Enable voice replies"} className="size-8 text-white/60" onClick={() => setVoiceEnabled((value) => !value)} size="icon" variant="ghost">{voiceEnabled ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}</Button>
        </div>
      </div>

      {!secret ? (
        <div className="grid flex-1 place-items-center p-8 text-center">
          <div className="max-w-sm">
            <div className="mx-auto grid size-14 place-items-center rounded-2xl border border-cyan-300/15 bg-cyan-300/7"><LockKeyhole className="size-6 text-cyan-200" /></div>
            <h3 className="mt-4 text-lg font-bold">Your key stays yours</h3>
            <p className="mt-2 text-sm leading-6 text-white/50">Connect a wallet and add an OpenRouter or compatible key. It is encrypted on this device, never stored by AniSoul.</p>
            {hasSavedSecret ? (
              <Button className="mt-5 bg-cyan-300 text-[#06100f] hover:bg-cyan-200" disabled={vaultBusy} onClick={() => void unlock()}>{vaultBusy ? "Waiting for signature" : "Unlock saved AI"}</Button>
            ) : (
              <p className="mt-5 text-xs text-cyan-200/70">Use “Set AI” in the top bar to begin.</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <Conversation className="min-h-0 flex-1">
            <ConversationContent className="gap-5 p-4 sm:p-5">
              {messages.length === 0 ? (
                <ConversationEmptyState
                  description="Start with a memory, preference, or the kind of companion you want."
                  icon={<Bot className="size-7" />}
                  title="Say hello to the unformed soul"
                />
              ) : messages.map((message) => (
                <Message from={message.role} key={message.id}>
                  <MessageContent className={message.role === "assistant" ? "border border-cyan-300/10 bg-cyan-300/[.045]" : "bg-white/8"}>
                    {message.content ? <MessageResponse>{message.content}</MessageResponse> : <span className="inline-flex items-center gap-2 text-sm text-white/45"><LoaderCircle className="size-4 animate-spin" />Thinking</span>}
                  </MessageContent>
                </Message>
              ))}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          <div className="border-t border-white/8 p-3 sm:p-4">
            {personality && (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-emerald-300/15 bg-emerald-300/6 px-3 py-2 text-xs text-emerald-100">
                <span className="inline-flex items-center gap-2"><Check className="size-4" />Personality forged: <strong>{personality.name}</strong></span>
                <span className="hidden text-emerald-100/50 sm:block">512-byte on-chain ready</span>
              </div>
            )}
            {userTurns >= 5 && !personality && (
              <Button className="mb-3 w-full border-pink-300/20 bg-pink-300/8 text-pink-100 hover:bg-pink-300/12" disabled={forging} onClick={() => void handleForge()} variant="outline">
                {forging ? <LoaderCircle className="size-4 animate-spin" /> : <Sparkles className="size-4" />}{forging ? "Forging personality" : "Forge personality card"}
              </Button>
            )}
            <PromptInput onSubmit={handleSubmit}>
              <PromptInputBody>
                <PromptInputTextarea maxLength={1_500} onChange={(event) => setInput(event.target.value)} placeholder="Talk to your avatar…" value={input} />
              </PromptInputBody>
              <PromptInputFooter>
                <PromptInputTools>
                  <SpeechInput
                    aria-label="Speak to avatar"
                    onAudioRecorded={transcribeWithWhisper}
                    onTranscriptionChange={(text) => setInput((value) => `${value} ${text}`.trim())}
                    variant="ghost"
                  />
                  <span className="text-[10px] text-white/35">Voice models load from Hugging Face</span>
                </PromptInputTools>
                <PromptInputSubmit disabled={!input.trim() || status === "streaming"} status={status} />
              </PromptInputFooter>
            </PromptInput>
            {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
          </div>
        </>
      )}
    </section>
  );
}
